package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.BorrowCandidate
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.HoursRange
import com.evoapps.evoshift.domain.model.WorkplaceGuest
import com.evoapps.evoshift.domain.repository.BorrowingRepository
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.coroutines.cancellation.CancellationException

/**
 * SH-267: the borrowCandidates callable (functions/borrowing.js) for the person picker, and
 * workplaces/{id}/guests (the managers list them; firestore.rules) for "Guests this month".
 */
class BorrowingRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient
) : BorrowingRepository {
    private val json = Json { ignoreUnknownKeys = true; coerceInputValues = true }

    @Serializable
    private data class HoursDto(val start: String = "", val end: String = "")

    @Serializable
    private data class DayDto(val status: String = "", val hours: List<HoursDto> = emptyList())

    @Serializable
    private data class CandidateDto(
        val userId: String = "",
        val displayName: String = "",
        val photoUrl: String? = null,
        val homeWorkplaceId: String = "",
        val homeWorkplaceName: String = "",
        val role: String? = null,
        val skills: List<String> = emptyList(),
        val monthHours: Double = 0.0,
        val days: Map<String, DayDto> = emptyMap(),
        val awayDays: List<Int> = emptyList()
    )

    @Serializable
    private data class CandidatesDto(val candidates: List<CandidateDto> = emptyList())

    @Serializable
    private data class GuestDto(val userId: String = "", val homeWorkplaceId: String? = null, val months: List<String> = emptyList())

    override suspend fun candidates(workplaceId: String, year: Int, month: Int): Result<List<BorrowCandidate>> = guarded {
        val result = functions.call("borrowCandidates", buildJsonObject {
            put("workplaceId", workplaceId)
            put("month", "$year-${month.toString().padStart(2, '0')}")
        })
        val dto = (result as? JsonObject)?.let { json.decodeFromJsonElement(CandidatesDto.serializer(), it) } ?: CandidatesDto()
        dto.candidates.filter { it.userId.isNotBlank() && it.homeWorkplaceId.isNotBlank() }.map(::toDomain)
    }

    override suspend fun guests(workplaceId: String, month: String): Result<List<WorkplaceGuest>> = guarded {
        firestore.collection("workplaces").document(workplaceId).collection("guests")
            .where { "months" contains month }.get()
            .documents.map { doc -> doc.data<GuestDto>().let { WorkplaceGuest(it.userId.ifBlank { doc.id }, it.homeWorkplaceId, it.months) } }
    }

    private inline fun <T> guarded(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        Result.failure(e)
    }

    private fun toDomain(dto: CandidateDto): BorrowCandidate = BorrowCandidate(
        userId = dto.userId,
        displayName = dto.displayName,
        photoUrl = dto.photoUrl,
        homeWorkplaceId = dto.homeWorkplaceId,
        homeWorkplaceName = dto.homeWorkplaceName,
        role = dto.role?.takeIf { it.isNotBlank() },
        skills = dto.skills,
        monthHours = dto.monthHours,
        days = dto.days.mapNotNull { (day, value) ->
            val number = day.toIntOrNull() ?: return@mapNotNull null
            val status = AvailabilityStatus.entries.firstOrNull { it.name == value.status } ?: return@mapNotNull null
            number to DayPreference(status, preferredSlotId = null, comment = null, hours = value.hours.map { HoursRange(it.start, it.end) })
        }.toMap(),
        awayDays = dto.awayDays.toSet()
    )
}
